const PAID_PAYMENT_STATUSES = new Set(["paid", "completed", "succeeded", "success"]);
const REFUNDED_PAYMENT_STATUSES = new Set(["refunded", "refund_completed", "refund_succeeded"]);
const CANCELLED_ORDER_STATUSES = new Set(["cancelled", "canceled"]);
const REFUNDED_ORDER_STATUSES = new Set(["refunded"]);
const COMPLETED_REFUND_STATUSES = new Set(["completed", "succeeded", "success"]);
const PARTIAL_REFUND_STATUSES = new Set(["partially_refunded", "partial_refund"]);

const parsePresentAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

const getSquareGrossAmount = (order) => {
  for (const value of [
    order?.square_amount,
    order?.gross_price_before_credit_card_fee,
    order?.total_price,
    order?.price,
  ]) {
    const amount = Number(value);
    if (Number.isFinite(amount) && amount > 0) return amount;
  }
  return 0;
};

export function getAdminOrderDisplayAmount(order) {
  if (!isOrderPaidBySquareOrAdmin(order) && (order?.manual_price_applied || order?.price_locked)) {
    for (const value of [order?.overridden_total_price, order?.manual_price, order?.price, order?.total_price]) {
      const amount = parsePresentAmount(value);
      if (amount !== null) return amount;
    }
  }

  if (hasSquarePaymentTracking(order) && !isOrderPaidBySquareOrAdmin(order)) {
    for (const value of [
      order?.gross_price_before_credit_card_fee,
      order?.square_amount,
      order?.payment_page_charge_amount_jpy,
      order?.retail_final_payment_jpy,
      order?.overridden_total_price,
      order?.price,
      order?.total_price,
    ]) {
      const amount = parsePresentAmount(value);
      if (amount !== null) return amount;
    }
  }

  const explicitNetAmount = parsePresentAmount(order?.retail_net_amount_after_card_fee_jpy);
  if (explicitNetAmount !== null && explicitNetAmount >= 0) return explicitNetAmount;

  const finalMemberPayment = parsePresentAmount(order?.retail_final_payment_jpy);
  if (finalMemberPayment !== null && finalMemberPayment >= 0) {
    const cardFee = order?.credit_card_fee_applied_to_price
      ? Math.max(0, Number(order?.credit_card_fee_amount || 0))
      : 0;
    return Math.max(0, finalMemberPayment - cardFee);
  }

  for (const value of [order?.overridden_total_price, order?.price, order?.total_price]) {
    const amount = parsePresentAmount(value);
    if (amount !== null) return amount;
  }
  return null;
}

export function isAdminGuestOrder(order) {
  return Boolean(order?.created_by_admin && !order?.user_email);
}

export function isLinkedTravelAgencyOrder(order) {
  return Boolean(
    (order?.user_id || order?.user_email) &&
    !order?.retail_member_id &&
    !order?.retail_member_email &&
    order?.sales_channel !== "retail_public"
  );
}

export function isOrderPaidBySquareOrAdmin(order) {
  const paymentStatus = String(order?.payment_status || "").trim().toLowerCase();
  const squarePaymentStatus = String(order?.square_payment_status || "").trim().toLowerCase();
  if (hasSquarePaymentTracking(order)) {
    return Boolean(order?.square_payment_id || PAID_PAYMENT_STATUSES.has(squarePaymentStatus));
  }
  if (paymentStatus === "unpaid") return false;
  return Boolean(
    order?.payment_already_received ||
    PAID_PAYMENT_STATUSES.has(paymentStatus)
  );
}

export function isOrderCancelledAndRefunded(order) {
  const orderStatus = String(order?.status || "").trim().toLowerCase();
  const paymentStatus = String(order?.payment_status || "").trim().toLowerCase();
  const squarePaymentStatus = String(order?.square_payment_status || "").trim().toLowerCase();
  const squareRefundStatus = String(order?.square_refund_status || order?.refund_status || "").trim().toLowerCase();
  const grossAmount = getSquareGrossAmount(order);
  const completedAmount = Number(order?.refund_completed_amount || 0);
  return Boolean(
    REFUNDED_ORDER_STATUSES.has(orderStatus) ||
    REFUNDED_PAYMENT_STATUSES.has(paymentStatus) ||
    REFUNDED_PAYMENT_STATUSES.has(squarePaymentStatus) ||
    REFUNDED_PAYMENT_STATUSES.has(squareRefundStatus) ||
    (grossAmount > 0 && completedAmount >= grossAmount)
  );
}

export function getAdminOrderRefundCancellationBadges(order) {
  const orderStatus = String(order?.status || "").trim().toLowerCase();
  const paymentStatus = String(order?.payment_status || "").trim().toLowerCase();
  const squarePaymentStatus = String(order?.square_payment_status || "").trim().toLowerCase();
  const squareRefundStatus = String(order?.square_refund_status || order?.refund_status || "").trim().toLowerCase();
  const refundStatus = String(order?.refund_status || order?.payment_status || "").trim().toLowerCase();

  const isCancelled = Boolean(
    CANCELLED_ORDER_STATUSES.has(orderStatus) ||
    order?.cancelled_at ||
    order?.cancel_reason ||
    order?.cancelled_by
  );
  const isRefunded = Boolean(
    REFUNDED_ORDER_STATUSES.has(orderStatus) ||
    REFUNDED_PAYMENT_STATUSES.has(paymentStatus) ||
    REFUNDED_PAYMENT_STATUSES.has(squarePaymentStatus) ||
    REFUNDED_PAYMENT_STATUSES.has(squareRefundStatus) ||
    (COMPLETED_REFUND_STATUSES.has(squareRefundStatus) && !PARTIAL_REFUND_STATUSES.has(refundStatus))
  );
  const isPartiallyRefunded = Boolean(
    PARTIAL_REFUND_STATUSES.has(paymentStatus) ||
    PARTIAL_REFUND_STATUSES.has(refundStatus) ||
    Number(order?.refund_completed_amount || 0) > 0
  ) && !isRefunded;

  const badges = [];
  if (isRefunded) badges.push("已退款");
  if (isPartiallyRefunded) badges.push("部分退款");
  if (isCancelled || isRefunded) badges.push("订单已取消");
  return badges;
}

export function hasSquarePaymentTracking(order) {
  const squarePaymentStatus = String(order?.square_payment_status || "").trim().toLowerCase();
  return Boolean(
    order?.square_payment_link_id ||
    order?.square_payment_url ||
    order?.payment_url ||
    order?.square_checkout_order_id ||
    order?.square_order_id ||
    order?.square_payment_id ||
    (squarePaymentStatus && squarePaymentStatus !== "not_started")
  );
}

export function shouldShowAdminGuestUnpaidBadge(order) {
  return Boolean(
    isAdminGuestOrder(order) &&
    !isOrderPaidBySquareOrAdmin(order) &&
    (order?.admin_guest_payment_required || order?.payment_status === "unpaid" || order?.payment_status == null)
  );
}

export function shouldShowSquareUnpaidBadge(order) {
  return Boolean(
    hasSquarePaymentTracking(order) &&
    !isOrderPaidBySquareOrAdmin(order) &&
    !isOrderCancelledAndRefunded(order)
  );
}

export function getAdminOrderPaymentStatusLabel(order) {
  if (isOrderCancelledAndRefunded(order)) return "已退款已取消";
  const paymentStatus = String(order?.payment_status || order?.refund_status || "").toLowerCase();
  if (PARTIAL_REFUND_STATUSES.has(paymentStatus) || Number(order?.refund_completed_amount || 0) > 0) {
    const orderStatus = String(order?.status || "").toLowerCase();
    return CANCELLED_ORDER_STATUSES.has(orderStatus) ? "部分退款已取消" : "部分退款";
  }
  if (order?.refund_in_progress || order?.status === "refund_processing") return "退款处理中";
  if (order?.refund_status === "failed" || order?.status === "refund_exception") return "退款异常";
  if (
    String(order?.customer_overtime_payment_status || "").trim().toLowerCase() === "unpaid" &&
    Number(order?.customer_overtime_amount_due || 0) > 0 &&
    isOrderPaidBySquareOrAdmin(order)
  ) return "原订单已付款 · 超时费待收";
  if (isOrderPaidBySquareOrAdmin(order)) return "已付款";
  const customerBillingType = String(order?.customer_billing_type || "").trim().toLowerCase();
  if (customerBillingType === "monthly" && !hasSquarePaymentTracking(order)) return "月结";
  if (!customerBillingType && isLinkedTravelAgencyOrder(order) && !hasSquarePaymentTracking(order)) return "";
  if (paymentStatus === "unpaid") return "未付款";
  if (shouldShowAdminGuestUnpaidBadge(order) || shouldShowSquareUnpaidBadge(order)) return "未付款";
  const squareStatus = String(order?.square_payment_status || order?.payment_status || "").toLowerCase();
  if (squareStatus === "payment_setup_required") return "Square未配置";
  if (squareStatus === "failed" || squareStatus === "cancelled") return "付款异常";
  if (squareStatus === "pending" || squareStatus === "open") return "等待付款";
  return "未生成链接";
}

export function getAdminOrderPaymentBadgeClass(order) {
  const label = getAdminOrderPaymentStatusLabel(order);
  if (label === "已付款") return "is-ok";
  if (label === "原订单已付款 · 超时费待收") return "is-warn";
  if (label === "未付款" || label === "付款异常" || label === "Square未配置") return "is-danger";
  if (["已退款已取消", "部分退款", "部分退款已取消", "退款处理中", "等待付款"].includes(label)) return "is-warn";
  if (label === "退款异常") return "is-danger";
  return "";
}

export function shouldShowAdminOrderPaymentStatus(order) {
  const label = getAdminOrderPaymentStatusLabel(order);
  return Boolean(label && label !== "未生成链接");
}

export function getAdminOrderPaymentDisplay(order, { accountName = "" } = {}) {
  const paymentLabel = getAdminOrderPaymentStatusLabel(order);
  const isPaidOrRefunded = ["已付款", "原订单已付款 · 超时费待收", "已退款已取消", "部分退款", "部分退款已取消", "退款处理中", "退款异常"].includes(paymentLabel);
  const accountEmail = String(order?.user_email || "").trim();
  const accountId = String(order?.user_id || "").trim();
  const accountParts = [String(accountName || "").trim(), accountEmail || accountId]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index);

  const statusLabel = paymentLabel === "月结"
    ? accountParts.length > 0
      ? `月结 · 旅行社账号：${accountParts.join(" / ")}`
      : "月结"
    : isPaidOrRefunded
    ? paymentLabel
    : accountParts.length > 0
      ? `旅行社账号：${accountParts.join(" / ")}`
      : "未付款";

  const squarePaymentId = String(order?.square_payment_id || "").trim();
  const squarePaidAt = order?.square_paid_at || "";
  const squareStatus = String(order?.square_payment_status || "").trim();
  const squareCurrency = String(order?.square_currency || "").trim();
  const squareReceiptUrl = String(order?.square_receipt_url || "").trim();
  const hasSquareAmount = order?.square_amount !== undefined && order?.square_amount !== null && order?.square_amount !== "";
  const hasSquareDetails = Boolean(
    squarePaymentId ||
    squarePaidAt ||
    squareStatus ||
    hasSquareAmount ||
    squareCurrency ||
    squareReceiptUrl
  );

  return {
    statusLabel,
    square: hasSquareDetails
      ? {
          paymentId: squarePaymentId,
          paidAt: squarePaidAt,
          status: squareStatus,
          amount: hasSquareAmount ? Number(order.square_amount) : null,
          currency: squareCurrency,
          receiptUrl: squareReceiptUrl,
        }
      : null,
  };
}

export function getAdminOrderRefundSummary(order) {
  const completedAmount = Math.max(0, Number(order?.refund_completed_amount || 0));
  const pendingAmount = Math.max(0, Number(order?.refund_pending_amount || 0));
  const paymentStatus = String(order?.payment_status || "").trim().toLowerCase();
  const refundStatus = String(order?.refund_status || order?.square_refund_status || "").trim().toLowerCase();
  const hasPaidFunds = Boolean(
    isOrderPaidBySquareOrAdmin(order) ||
    REFUNDED_PAYMENT_STATUSES.has(paymentStatus) ||
    PARTIAL_REFUND_STATUSES.has(paymentStatus) ||
    REFUNDED_PAYMENT_STATUSES.has(refundStatus) ||
    PARTIAL_REFUND_STATUSES.has(refundStatus) ||
    completedAmount > 0 ||
    pendingAmount > 0 ||
    order?.refund_in_progress ||
    order?.status === "refund_processing"
  );
  const paidAmount = hasPaidFunds ? getSquareGrossAmount(order) : 0;
  const remainingAmount = Math.max(0, paidAmount - completedAmount - pendingAmount);
  return {
    paidAmount,
    completedAmount,
    pendingAmount,
    remainingAmount,
    inProgress: Boolean(order?.refund_in_progress || order?.status === "refund_processing"),
    canRefund: Boolean(order?.square_payment_id && remainingAmount > 0 && !order?.refund_in_progress && order?.status !== "refund_processing"),
    latestRefundId: String(order?.square_refund_id || "").trim(),
    latestRefundStatus: String(order?.square_refund_status || order?.refund_status || "").trim(),
    failureReason: String(order?.refund_exception_reason || "").trim(),
  };
}
